<template>
  <div class="PlayerMain">
    <player-loading v-if="isLoading" />

    <!-- Unpublished video -->
    <template v-else>
      <player-unpublished-video
        v-if="showErrorMsg"
        :isUsingIframe="isUsingIframe"
        :isMobileDevice="IS_MOBILE"
        :isEmbedDeephow="isEmbedDeephow"
        :isOpenedAsPopup="isNewPlayer || isSkillPlayer || isSearchPlayer"
        @close-player="closePlayer"
      >
        <span v-if="isDeleted" v-html="$t('player.thisWorkflowWasDeleted')"></span>
        <span v-else-if="isNotExists" v-html="$t('player.thisWorkflowDoesNotExist')"></span>
      </player-unpublished-video>

      <component
        v-else
        :is="IS_MOBILE ? 'player-processed-video-mobile' : 'player-processed-video'"
        :ref="IS_MOBILE ? 'PlayerProcessedVideoPhone' : 'PlayerProcessedVideo'"
        :workflow="workflowData"
        :steps="getters_steps"
        :attachmentsData="getter_attachments"
        :stepId="stepId"
        :startTimestamp="startTimestamp"
        :isSkillPlayer="isSkillPlayer"
        :showSkillNext="showSkillNext"
        :isSearchPlayer="isSearchPlayer"
        :isNewPlayer="isNewPlayer"
        :hideCloseBtn="hideCloseBtn"
        :isUsingIframe="isUsingIframe"
        :iframeFullscreen="iframeFullscreen"
        :isEmbedDeephow="isEmbedDeephow"
        :isPlayerAttachmentShowUp.sync="isHidePlayerAnnotation"
        :showTopBarForcible="showTopBarForcible"
        @closePlayerEvent="closePlayer"
        @workflow-ended="$emit('workflow-ended')"
        @fetchVideoDataAndCurrentStepIndex="fetchVideoDataAndCurrentStepIndex"
      >
        <template slot="annotation">
          <PlayerAnnotation
            :isHideCanvas.sync="isHidePlayerAnnotation"
            :workflow="workflowData"
            :videoData="videoData"
          />
        </template>
      </component>
    </template>
  </div>
</template>

<script>
import PlayerLoading from "@/components/DWorkflowPlayer/player/message/PlayerLoading.vue";
import PlayerProcessedVideo from "@/components/DWorkflowPlayer/player/PlayerProcessedVideo.vue";
import PlayerProcessedVideoMobile from "@/components/DWorkflowPlayer/player/PlayerProcessedVideoMobile.vue";
import MixinDB from "@/components/MixinDB.vue";
import MixinUser from "@/components/MixinUser.vue";
import PlayerUnpublishedVideo from "@/components/DPlayer/PlayerUnpublishedVideo.vue";
import Analytics from "@/js/analytics/analytics";
import MixinSkills from "@/components/MixinSkills";
import PlayerAnnotation from "@/components/DPlayer/PlayerAnnotation";
import { IS_MOBILE } from "@/constants/device-version";
import { checkCodeIsNotAllowIp } from "@/js/login/checkRestrictedIp";
import { authWorkflowPermission } from "@/server/api-server";
import { mapActions, mapState, mapGetters } from "vuex";

export default {
  name: "PlayerMain",
  // note: this is a copy of "@/components/PlayerMain.vue" and removed unsed code
  // if these are some bugs could compare to the old version
  // the old version will be removed when new version is stable
  props: {
    isSkillPlayer: {
      type: Boolean,
    },
    skillWorkflowId: {
      type: String,
    },
    showSkillNext: {
      type: Boolean,
    },
    isSearchPlayer: {
      type: Boolean,
    },
    isNewPlayer: {
      type: Boolean,
    },
    searchWorkflowId: {
      type: String,
    },
    searchStepId: {
      type: String,
    },
    startTimestamp: {
      type: Number,
    }, //route param that allows user to jump to a specific timestamp in a step
    hideCloseBtn: {
      type: Boolean,
    },
    showTopBarForcible: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [MixinDB, MixinUser, MixinSkills],
  components: {
    PlayerLoading,
    PlayerProcessedVideo,
    PlayerProcessedVideoMobile,
    PlayerUnpublishedVideo,
    PlayerAnnotation,
  },
  watch: {},
  data() {
    const videoData = {
      clientWidth: 0,
      clientHeight: 0,
      currentStepIndex: 0,
      currentVideoTime: 0,
    };
    return {
      IS_MOBILE,
      videoData,
      workflowId: "",
      stepId: "", //route param that allow user to jump to the specific step
      isUsingIframe: false,
      iframeFullscreen: false,
      isEmbedDeephow: false,
      isDeleted: false,
      isNotExists: false,
      isHistoryStateChanged: false,
      countApi: 0,
      isHidePlayerAnnotation: false,
      isLoadingWorkflowData: true,
      isLoadingOrganization: true,
    };
  },

  async created() {
    if (this.isSkillPlayer) {
      this.workflowId = this.skillWorkflowId;
    } else if (this.isSearchPlayer || this.isNewPlayer) {
      this.workflowId = this.searchWorkflowId;
      this.stepId = this.searchStepId || this.$route.params.stepId;
    } else {
      this.workflowId = this.$route.params.workflowId;
      this.stepId = this.$route.params.stepId;
    }

    this.auth().onAuthStateChanged((user) => {
      if (!user) {
        this.$router.push({
          name: "Auth",
          query: { redirect: "/playermain/" + this.workflowId },
        });
      }
    });

    const { ok, data, code } = await authWorkflowPermission(this.workflowId);
    if (!ok || !data.item.allowView) {
      if (!checkCodeIsNotAllowIp(code)) {
        this.$router.push({ name: "403" });
      }
      return;
    }

    this.checkUrlMatchPlayerMain();

    const { isSuccess } = await this.fetchWorkflowData({ workflowId: this.workflowId });
    this.isLoadingWorkflowData = false;
    if (isSuccess) {
      const { isPreviewVideoMode } = this.$route.params;
      if (!this.workflowData.published && !isPreviewVideoMode) {
        // unpublished workflow can only play by Preview Video Mode
        this.showErrorMessage();
        this.clearWorkflowData();
      }
    } else {
      this.showErrorMessage();
    }

    // check if it's using iframe
    if (window.self != window.top) {
      if (this.$route.name == "PlayerEmbed") {
        // "PlayerEmbed" is for the iFrame iFrame on DPopupPlayer, only use inside DeepHow
        // UI is the same as Theater Mode
        this.isUsingIframe = false; // don't use iFrame controls
        this.isEmbedDeephow = true;
        this.enterTheaterMode();
      } else {
        // for inseted iFrame player on other websites, like ABI is using on Acadia
        this.isUsingIframe = true;
      }
      this.iframeFullscreen = false;
      if (this.isUsingIframe) {
        window.addEventListener("fullscreenchange", this.fullscreenChangeHandler);
      }
    }
    this.getTrackingDocumentAndSetAnalyticsTrack();
  },
  updated() {},
  beforeDestroy() {
    Analytics.removeEventProperties("Workflow ID");
    Analytics.removeEventProperties("Workspace ID");
    if (this.isHistoryStateChanged) history.back();
    window.removeEventListener("fullscreenchange", this.fullscreenChangeHandler);
    this.clearAllWorkflowPlayerData();
  },
  methods: {
    ...mapActions("workflowPlayer", [
      "fetchWorkflowData",
      "clearWorkflowData",
      "showErrorMessage",
      "clearAllWorkflowPlayerData",
      "enterTheaterMode",
    ]),
    closeVideoPlayer() {
      if (IS_MOBILE && this.$refs.PlayerProcessedVideoPhone) {
        this.$refs.PlayerProcessedVideoPhone.closeVideoPlayer();
        return;
      }
      if (this.$refs.PlayerProcessedVideo) {
        this.$refs.PlayerProcessedVideo.closeVideoPlayer();
      }
    },
    fullscreenChangeHandler() {
      this.iframeFullscreen = !this.iframeFullscreen;
      if (this.iframeFullscreen) {
        this.$refs.PlayerProcessedVideo.playing = true;
      } else {
        this.$refs.PlayerProcessedVideo.closeAllComponents();
        this.$refs.PlayerProcessedVideo.playing = false;
      }
    },
    async getTrackingDocumentAndSetAnalyticsTrack() {
      try {
        const data = await this.getUserWorkflowTracking({ uid: this.$user.uid }, { id: this.workflowId });
        Analytics.setTrack(
          "Web - General - Watch - Workflow",
          {
            "Workflow ID": this.workflowId,
            "Firestore ID": data[0].id,
            "Workspace ID": this.workflowData.group,
          },
          {
            category: "PlayerMain",
            action: "Watch Workflow",
            name: "Watch Workflow",
            params: {
              dimension3: this.workflowId,
              dimension4: data[0].id,
              dimension5: this.workflowData.group,
            },
          }
        );
        Analytics.setEventProperties({ "Workflow ID": this.workflowId, "Workspace ID": this.workflowData.group });
      } catch (e) {
        if (e.code === "permission-denied" && this.countApi < 3) {
          setTimeout(() => {
            this.getTrackingDocumentAndSetAnalyticsTrack();
          }, 3000);
        }
      } finally {
        this.countApi++;
      }
    },
    closePlayer: function () {
      if (this.isNewPlayer || this.isSkillPlayer) {
        this.$emit("close-search-player");
      } else {
        this.$router.push("/navigator/home");
      }
    },
    checkUrlMatchPlayerMain() {
      if (!location.pathname.match(/\/playermain/)) {
        this.isHistoryStateChanged = true;
        history.pushState({}, null, "/playermain/" + this.workflowId);
      }
    },
    fetchVideoDataAndCurrentStepIndex(videoData) {
      for (const key in videoData) {
        if (Object.prototype.hasOwnProperty.call(videoData, key)) {
          this.videoData[key] = videoData[key];
        }
      }
    },
  },
  computed: {
    ...mapState("workflowPlayer", ["workflowData", "showErrorMsg"]),
    ...mapGetters("workflowPlayer", ["getters_steps", "getter_attachments"]),
    ...mapGetters(["getter_organization"]),
    isLoading() {
      return this.isLoadingOrganization || this.isLoadingWorkflowData;
    },
  },
  watch: {
    getter_organization: {
      handler(getter_organization) {
        if (getter_organization) this.isLoadingOrganization = false;
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.PlayerMain {
  position: fixed;
  inset: 0;
  background: #242527;
  align-items: center;
  justify-content: center;
  display: flex;
}
</style>
